import React, { useEffect, useState } from 'react';
import { CreateExtComponent } from '@powerednow/client-shared/Components/react/React';
import BaseDialog from '@powerednow/components/Dialog/Dialog';
import CircularProgress from '@powerednow/components/CircularProgress/CircularProgress';
import DocumentEntity from '@powerednow/models/document/entity';
import Document from '@powerednow/models/document';
import {
    DocumentEntityWithAlreadyInvoicedAmount,
    getDocumentHooks,
} from '@powerednow/models/document/hooks';
import { type ColorTypes } from '@powerednow/components/button/ColoredButton';
import useComplexData from 'FieldServices/hooks/useComplexData';
import { DOCUMENT } from '@powerednow/shared/constants';
import Box from '@powerednow/components/Box/Box';
import Bluebird from 'bluebird';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { Theme } from '@mui/material/styles';
import { useDataWrapper } from '@powerednow/components/DataWrapperProvider/DataWrapperProvider';
import { DataWrapperInterface } from '@powerednow/complexData/connection/api/dataWrapper';
import QuoteList from './QuoteList';
import withDataWrapper from '../../../hooks/withDataWrapper';
import PurchaseOrderList from './PurchaseOrderList';

interface DocumentSelectBoxProps {
    documentTypeId: number,
    customerId: number,
    supplierId: number,
    onDocumentSelected: (document: DocumentEntity) => void,
    onClose: () => void,
    onSkip: () => void,
}

const { useQuotesByCustomer, usePurchaseOrdersByCustomer } = getDocumentHooks(useComplexData);

const useStyles = makeStyles<Theme>(createStyles({
    container: {
        paddingTop: '20px',
        paddingBottom: '20px',
    },
}));

function DocumentSelectBox(props: DocumentSelectBoxProps) {
    const {
        onClose,
        onSkip,
        onDocumentSelected,
        customerId,
        documentTypeId,
        supplierId = 0,
    } = props;
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const isUsingPurchaseOrderLogic = documentTypeId === DOCUMENT.TYPES_VALUES.SUPPLIER_INVOICE;
    const title = isUsingPurchaseOrderLogic ? 'Use an Existing Purchase Order?' : 'Use an Existing Quote?';
    const skipButtonText = documentTypeId === DOCUMENT.TYPES_VALUES.PURCHASE_ORDER ? 'No, create a new Purchase Order' : 'No, create a new Invoice';
    const { optionalResult } = isUsingPurchaseOrderLogic ? usePurchaseOrdersByCustomer(customerId, supplierId) : useQuotesByCustomer(customerId);
    const dataWrapper: DataWrapperInterface = useDataWrapper();
    const [popupOpen, setPopupOpen] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);

    const close = () => {
        setOpen(false);
        onClose();
    };

    const skipAndClose = () => {
        onSkip();
        close();
    };

    useEffect(() => {
        if (optionalResult?.loaded) {
            if (optionalResult?.documents.length) {
                setOpen(true);
            } else {
                skipAndClose();
            }
        }
    }, [optionalResult]);

    const buttons = [{
        children: skipButtonText,
        onClick: () => skipAndClose(),
        disabled: !optionalResult?.loaded,
        color: 'secondary' as ColorTypes,
    }];

    async function shouldShowPopup(documentEntity): Promise<boolean> {
        const document = await dataWrapper.getComplexDataObject(Document, documentEntity.id);
        const hasTaxRates = await Bluebird.reduce(await document.getJobItemGroups(), async (acc, itemGroup) => {
            const hasJobItemTaxRate = await Bluebird.reduce(
                await itemGroup.getAllJobItem(),
                async (acc, jobItem) => acc && (!jobItem.data.use_tax || Boolean(await jobItem.getUserTaxRate())),
                true,
            );
            return acc && hasJobItemTaxRate;
        }, true);
        return !hasTaxRates;
    }

    const handleClick = async (document: DocumentEntityWithAlreadyInvoicedAmount | DocumentEntity) => {
        if (await shouldShowPopup(document)) {
            setPopupOpen(true);
            setSelectedDocument(document);
        } else {
            onDocumentSelected(document);
        }
    };

    const handlePopupClose = value => {
        setPopupOpen(false);
        onDocumentSelected(selectedDocument);
        close();
    };

    const ListComponent = isUsingPurchaseOrderLogic ? PurchaseOrderList : QuoteList;

    const popupButtons = [
        { children: 'Close', onClick: handlePopupClose, color: 'primary' as ColorTypes },
    ];

    return (
        <BaseDialog
            onClose={close}
            open={open}
            title={title}
            buttons={buttons}
            fullWidth
        >
            <Box>
                <ListComponent
                    renderIf={optionalResult?.loaded}
                    defaultElement={<CircularProgress />}
                    documents={optionalResult?.documents}
                    onCardClick={handleClick}
                    hasNotFullyPaidDocuments={optionalResult?.hasNotFullyPaidDocuments}
                />
                <BaseDialog
                    open={popupOpen}
                    onClose={handlePopupClose}
                    title="Warning"
                    buttons={popupButtons}
                >
                    <div className={classes.container}>
                        <p>The tax rate of some pricing on this document has been deleted. Therefore this document can not be displayed.</p>
                    </div>
                </BaseDialog>
            </Box>
        </BaseDialog>
    );
}

export default CreateExtComponent('FieldServices.view.modal.DocumentSelectBox.DocumentSelectBox', withDataWrapper(DocumentSelectBox));
